import type { Expr, SetLine, Stmt, Where } from './ast'
import { normalizeExerciseName } from './ast'
import { ScriptError, type Dim } from './lexer'
import {
  NIL,
  type Scope,
  arithmetic,
  arr,
  bool,
  compare,
  describeValue,
  num,
  same,
  truthy,
  txt,
  type Value,
} from './values'

/** `return` halting a block, per the spec — nothing after it is yielded. */
class Halt extends Error {
  readonly line: SetLine | null

  constructor(line: SetLine | null) {
    super('return')
    this.line = line
  }
}

/** How far a block may run before it is stopped. Every implementation stops at the same place. */
export const STEP_BUDGET = 100_000

/** What a script can call. Everything here answers for every input — nothing returns nothing. */
export class Builtins {
  /** Every set of an exercise ever logged, by normalized name, oldest first. */
  private readonly historyOf: (name: string) => readonly Value[]
  /** The best estimated one-rep max of an exercise, or null when nothing says. */
  private readonly bestE1rm: (name: string) => number | null
  private readonly currentExercise: string

  constructor(
    historyOf: (name: string) => readonly Value[],
    bestE1rm: (name: string) => number | null,
    currentExercise: string,
  ) {
    this.historyOf = historyOf
    this.bestE1rm = bestE1rm
    this.currentExercise = currentExercise
  }

  call(name: string, args: readonly Value[], at: Where, repsInHand: number | null): Value {
    const number = (index: number): number => {
      const value = args[index]
      if (value?.kind !== 'number') {
        throw new ScriptError(at.line, at.column, `\`${name}\` wants a number`)
      }
      return value.value
    }
    const dimOf = (): Dim => {
      const first = args.find((value) => value.kind === 'number')
      return first?.kind === 'number' ? first.dim : 'none'
    }

    switch (name) {
      case 'history': {
        const exercise = args[0]
        if (exercise?.kind !== 'text') {
          throw new ScriptError(at.line, at.column, '`history` takes the name of an exercise')
        }
        return arr(this.historyOf(normalizeExerciseName(exercise.value)))
      }

      case 'range': {
        const low = args[0]
        const high = args[1]
        if (low?.kind !== 'number' || high?.kind !== 'number') {
          throw new ScriptError(at.line, at.column, '`range` takes two numbers')
        }
        return { kind: 'span', low: low.value, high: high.value, open: false, dim: low.dim }
      }

      case 'amrap': {
        const only = number(0)
        return { kind: 'span', low: only, high: only, open: true, dim: 'none' }
      }

      case 'e1rm': {
        // `e1rm(85%, 60kg)` reads this exercise; naming one first reads that one.
        const first = args[0]
        const named = first?.kind === 'text' ? first.value : null
        const offset = named === null ? 0 : 1
        const percent = number(offset)
        const fallback = args[offset + 1]
        if (fallback?.kind !== 'number') {
          throw new ScriptError(at.line, at.column, '`e1rm` needs a weight to fall back on')
        }
        const best = this.bestE1rm(normalizeExerciseName(named ?? this.currentExercise))
        return best === null ? fallback : num(best * percent, 'weight')
      }

      case 'rpe': {
        const effort = number(0)
        const fallback = args[1]
        if (fallback?.kind !== 'number') {
          throw new ScriptError(at.line, at.column, '`rpe` needs a weight to fall back on')
        }
        if (repsInHand === null) {
          throw new ScriptError(
            at.line,
            at.column,
            '`rpe` belongs in the weight of a line asking for a plain number of reps',
          )
        }
        const best = this.bestE1rm(normalizeExerciseName(this.currentExercise))
        if (best === null) {
          return fallback
        }
        // Reps in reserve turn an effort into reps, and Epley turns those into a weight.
        const effective = repsInHand + (10 - effort)
        return num(best / (1 + effective / 30), 'weight')
      }

      case 'min':
        return num(
          Math.min(...args.map((value) => (value.kind === 'number' ? value.value : 0))),
          dimOf(),
        )
      case 'max':
        return num(
          Math.max(...args.map((value) => (value.kind === 'number' ? value.value : 0))),
          dimOf(),
        )
      case 'floor':
        return num(Math.floor(number(0)), dimOf())
      case 'ceil':
        return num(Math.ceil(number(0)), dimOf())
      case 'round':
        return num(Math.round(number(0)), dimOf())
      case 'abs':
        return num(Math.abs(number(0)), dimOf())

      default:
        throw new ScriptError(at.line, at.column, `\`${name}\` is not a name this block knows`)
    }
  }
}

/**
 * Runs the statements of a `dynamic` block, and evaluates the expressions a static line is
 * built from. It reads history and writes nothing.
 */
export class Interpreter {
  private steps = 0

  /** The reps of the line being built, which is what an `rpe` in its weight is asking about. */
  repsInHand: number | null = null

  private readonly roots: ReadonlyMap<string, Value>
  private readonly builtins: Builtins

  constructor(roots: ReadonlyMap<string, Value>, builtins: Builtins) {
    this.roots = roots
    this.builtins = builtins
  }

  private step(at: Where): void {
    if (++this.steps > STEP_BUDGET) {
      throw new ScriptError(
        at.line,
        at.column,
        `This block is still running after ${STEP_BUDGET} steps`,
      )
    }
  }

  /** Runs a block, handing each yielded line to `emit`. */
  run(body: readonly Stmt[], scope: Scope, emit: (line: SetLine, scope: Scope) => void): void {
    try {
      this.exec(body, scope, emit)
    } catch (error) {
      if (error instanceof Halt) {
        if (error.line) {
          emit(error.line, scope)
        }
        return
      }
      throw error
    }
  }

  private exec(
    body: readonly Stmt[],
    scope: Scope,
    emit: (line: SetLine, scope: Scope) => void,
  ): void {
    for (const statement of body) {
      this.step(statement.at)
      switch (statement.type) {
        case 'declare':
          scope.declare(statement.name, this.eval(statement.value, scope), statement.mutable)
          break

        case 'assign': {
          const target = statement.target
          if (target.type !== 'name') {
            throw new ScriptError(
              statement.at.line,
              statement.at.column,
              'Only a local can be assigned to',
            )
          }
          const value =
            statement.op === '='
              ? this.eval(statement.value, scope)
              : arithmetic(
                  statement.op.charAt(0),
                  this.eval(target, scope),
                  this.eval(statement.value, scope),
                  statement.at,
                )
          scope.assign(target.name, value, statement.at)
          break
        }

        case 'if':
          if (truthy(this.eval(statement.condition, scope))) {
            this.exec(statement.then, scope.child(), emit)
          } else {
            this.exec(statement.otherwise, scope.child(), emit)
          }
          break

        case 'for': {
          const loop = scope.child()
          if (statement.init) {
            this.exec([statement.init], loop, emit)
          }
          while (statement.condition === null || truthy(this.eval(statement.condition, loop))) {
            this.step(statement.at)
            this.exec(statement.body, loop.child(), emit)
            if (statement.step) {
              this.exec([statement.step], loop, emit)
            }
          }
          break
        }

        case 'forOf': {
          const source = this.eval(statement.source, scope)
          if (source.kind !== 'array') {
            throw new ScriptError(
              statement.at.line,
              statement.at.column,
              `Only a list can be walked, and this is ${describeValue(source)}`,
            )
          }
          for (const item of source.items) {
            this.step(statement.at)
            const loop = scope.child()
            loop.declare(statement.name, item, true)
            this.exec(statement.body, loop, emit)
          }
          break
        }

        case 'yield':
          emit(statement.line, scope)
          break

        case 'return':
          throw new Halt(statement.line)

        case 'expr':
          this.eval(statement.expr, scope)
          break
      }
    }
  }

  eval(expr: Expr, scope: Scope): Value {
    this.step(expr.at)
    switch (expr.type) {
      case 'number':
        return num(expr.value * expr.unit.factor, expr.unit.dim)

      case 'text':
        return txt(expr.value)

      case 'hole':
        throw new ScriptError(
          expr.at.line,
          expr.at.column,
          `\`$${expr.name}\` has nothing to stand for here`,
        )

      case 'name': {
        const found = scope.get(expr.name) ?? this.roots.get(expr.name)
        if (found === undefined) {
          throw new ScriptError(
            expr.at.line,
            expr.at.column,
            `\`${expr.name}\` is not a name this block knows`,
          )
        }
        return found
      }

      case 'array':
        return arr(expr.items.map((item) => this.eval(item, scope)))

      case 'arrow':
        return { kind: 'fn', params: expr.params, body: expr.body, scope }

      case 'unary': {
        const operand = this.eval(expr.operand, scope)
        if (expr.op === '!') {
          return bool(!truthy(operand))
        }
        if (operand.kind !== 'number') {
          throw new ScriptError(expr.at.line, expr.at.column, 'Only a number can be negated')
        }
        return num(-operand.value, operand.dim)
      }

      case 'ternary':
        return truthy(this.eval(expr.condition, scope))
          ? this.eval(expr.then, scope)
          : this.eval(expr.otherwise, scope)

      case 'binary': {
        switch (expr.op) {
          case '??': {
            const left = this.eval(expr.left, scope)
            return left.kind === 'nil' ? this.eval(expr.right, scope) : left
          }
          case '&&': {
            const left = this.eval(expr.left, scope)
            return truthy(left) ? this.eval(expr.right, scope) : left
          }
          case '||': {
            const left = this.eval(expr.left, scope)
            return truthy(left) ? left : this.eval(expr.right, scope)
          }
          case '===':
            return bool(same(this.eval(expr.left, scope), this.eval(expr.right, scope)))
          case '!==':
            return bool(!same(this.eval(expr.left, scope), this.eval(expr.right, scope)))
          case '<':
          case '>':
          case '<=':
          case '>=':
            return bool(
              compare(expr.op, this.eval(expr.left, scope), this.eval(expr.right, scope), expr.at),
            )
          default:
            return arithmetic(
              expr.op,
              this.eval(expr.left, scope),
              this.eval(expr.right, scope),
              expr.at,
            )
        }
      }

      case 'member': {
        const target = this.eval(expr.target, scope)
        if (target.kind === 'nil' && expr.optional) {
          return NIL
        }
        return this.member(target, expr.name, expr.at)
      }

      case 'index': {
        const target = this.eval(expr.target, scope)
        const index = this.eval(expr.index, scope)
        if (target.kind !== 'array') {
          throw new ScriptError(
            expr.at.line,
            expr.at.column,
            `Only a list can be indexed, and this is ${describeValue(target)}`,
          )
        }
        if (index.kind !== 'number') {
          throw new ScriptError(expr.at.line, expr.at.column, 'A list is indexed by a number')
        }
        return target.items[index.value] ?? NIL
      }

      case 'call':
        return this.call(expr, scope)
    }
  }

  private member(target: Value, name: string, at: Where): Value {
    switch (target.kind) {
      case 'obj': {
        const found = target.fields.get(name)
        if (found === undefined) {
          throw new ScriptError(at.line, at.column, `${target.name} has no \`${name}\``)
        }
        return found
      }
      case 'array':
        if (name === 'length') {
          return num(target.items.length)
        }
        throw new ScriptError(at.line, at.column, `A list has no \`${name}\``)
      case 'span':
        if (name === 'low') return num(target.low, target.dim)
        if (name === 'high') return num(target.high, target.dim)
        if (name === 'open') return bool(target.open)
        throw new ScriptError(at.line, at.column, `A range has no \`${name}\``)
      case 'nil':
        throw new ScriptError(at.line, at.column, `There is nothing here to read \`${name}\` from`)
      default:
        throw new ScriptError(at.line, at.column, `${describeValue(target)} has no \`${name}\``)
    }
  }

  private call(expr: Extract<Expr, { type: 'call' }>, scope: Scope): Value {
    const callee = expr.callee
    if (callee.type === 'member') {
      const target = this.eval(callee.target, scope)
      if (target.kind === 'array') {
        return this.arrayMethod(target.items, callee.name, expr, scope)
      }
    }
    if (callee.type === 'name') {
      const local = scope.get(callee.name)
      if (local?.kind === 'fn') {
        return this.invoke(
          local,
          expr.args.map((arg) => this.eval(arg, scope)),
          expr.at,
        )
      }
      return this.builtins.call(
        callee.name,
        expr.args.map((arg) => this.eval(arg, scope)),
        expr.at,
        this.repsInHand,
      )
    }
    const value = this.eval(callee, scope)
    if (value.kind === 'fn') {
      return this.invoke(
        value,
        expr.args.map((arg) => this.eval(arg, scope)),
        expr.at,
      )
    }
    throw new ScriptError(expr.at.line, expr.at.column, 'This is not something that can be called')
  }

  invoke(fn: Extract<Value, { kind: 'fn' }>, args: readonly Value[], at: Where): Value {
    this.step(at)
    const scope = fn.scope.child()
    fn.params.forEach((name, index) => {
      scope.declare(name, args[index] ?? NIL, false)
    })
    return this.eval(fn.body, scope)
  }

  private arrayMethod(
    items: readonly Value[],
    name: string,
    expr: Extract<Expr, { type: 'call' }>,
    scope: Scope,
  ): Value {
    const args = expr.args.map((arg) => this.eval(arg, scope))
    const first = args[0]
    const needsFn = (): Extract<Value, { kind: 'fn' }> => {
      if (first?.kind !== 'fn') {
        throw new ScriptError(
          expr.at.line,
          expr.at.column,
          `\`${name}\` needs a function to run over the list`,
        )
      }
      return first
    }

    switch (name) {
      case 'at': {
        if (first?.kind !== 'number') {
          throw new ScriptError(expr.at.line, expr.at.column, '`at` takes a number')
        }
        const position = first.value < 0 ? items.length + first.value : first.value
        return items[position] ?? NIL
      }
      case 'every':
        return bool(items.every((item) => truthy(this.invoke(needsFn(), [item], expr.at))))
      case 'some':
        return bool(items.some((item) => truthy(this.invoke(needsFn(), [item], expr.at))))
      case 'filter':
        return arr(items.filter((item) => truthy(this.invoke(needsFn(), [item], expr.at))))
      case 'find':
        return items.find((item) => truthy(this.invoke(needsFn(), [item], expr.at))) ?? NIL
      case 'map':
        return arr(items.map((item) => this.invoke(needsFn(), [item], expr.at)))
      case 'reduce': {
        const step = needsFn()
        let total = args[1] ?? NIL
        for (const item of items) {
          total = this.invoke(step, [total, item], expr.at)
        }
        return total
      }
      default:
        throw new ScriptError(expr.at.line, expr.at.column, `A list has no \`${name}\``)
    }
  }
}
